import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import ArvexisLogo from "../components/ArvexisLogo";
import ArvexisPresence from "../components/ArvexisPresence";
import BackToTop from "../components/BackToTop";
import { publicView } from "../content/aboutArvexisContent";
import "../styles/design-system-v2.css";

// The already-approved canonical Arvexis atmospheric background, as supplied, composed locally in
// exactly the form the accepted surfaces use it (B_Approved_background_v2 on phones,
// Canonical_Desktop_Background_v2 on desktop, over the same navy base). Nothing here is recoloured,
// regenerated or reinterpreted, and no shared component is touched. It is painted on a fixed
// viewport-sized layer rather than on the scrolling page so a 14,000px column cannot tile it.
const ATMOSPHERE = `
  linear-gradient(180deg, rgba(4,6,11,0.30) 0%, rgba(4,6,11,0.55) 100%),
  url('/B_Approved_background_v2.png') center center / cover no-repeat,
  linear-gradient(180deg, #070A12 0%, #05070E 62%, #04060B 100%)
`;

const DESKTOP_ATMOSPHERE = `
  linear-gradient(180deg, rgba(4,6,11,0.30) 0%, rgba(4,6,11,0.55) 100%),
  url('/Canonical_Desktop_Background_v2.png') center center / cover no-repeat,
  linear-gradient(180deg, #070A12 0%, #04060B 100%)
`;

const BASE_NAVY = "#04060B";

const SERIF = "'Cormorant Garamond', Georgia, serif";
const BODY = "'Inter', system-ui, sans-serif";
const CHAMPAGNE = "#E8C58F";
const IVORY = "#F4EFE7";
const SILVER = "#A8B3BF";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const Paragraph = ({ children }) => (
  <p
    className="text-[15px] sm:text-base lg:text-[17px] leading-[1.9] mb-6 last:mb-0"
    style={{ fontFamily: BODY, color: SILVER }}
  >
    {children}
  </p>
);

const Lines = ({ items }) => (
  <div className="mb-7 last:mb-0 space-y-2.5 pl-5 border-l border-[#E8C58F]/20">
    {items.map((line, i) => (
      <p
        key={i}
        className="text-[15px] sm:text-base leading-[1.75]"
        style={{ fontFamily: BODY, color: "rgba(168,179,191,0.92)" }}
      >
        {line}
      </p>
    ))}
  </div>
);

// A canonical pull quote, given room to breathe. This is the pacing device of the public surface.
// Two weights on purpose: `major` for the few landmark lines, `minor` for chapter closings — if
// every quote were monumental, none of them would be.
const Interstitial = ({ text, tone = "ivory", weight = "minor" }) => {
  const major = weight === "major";
  return (
    <motion.div
      {...fadeUp}
      className={major ? "px-6 sm:px-8 py-20 sm:py-28 lg:py-36" : "px-6 sm:px-8 py-10 sm:py-14 lg:py-20"}
      data-testid={`about-public-interstitial-${weight}`}
    >
      <div className={`${major ? "max-w-3xl text-center" : "max-w-xl lg:max-w-[38rem]"} mx-auto`}>
        {major && (
          <div
            className="w-10 h-px mx-auto mb-10"
            style={{ background: "rgba(232,197,143,0.45)" }}
            aria-hidden="true"
          />
        )}
        <p
          className={
            major
              ? "text-[1.5rem] sm:text-[2rem] lg:text-[2.625rem] leading-[1.35] tracking-[-0.01em] whitespace-pre-line"
              : "text-[1.125rem] sm:text-[1.25rem] lg:text-[1.5rem] leading-[1.5] whitespace-pre-line"
          }
          style={{ fontFamily: SERIF, fontWeight: 300, color: tone === "gold" ? CHAMPAGNE : IVORY }}
        >
          {text}
        </p>
      </div>
    </motion.div>
  );
};

const Breath = () => (
  <div className="relative h-14 sm:h-20 lg:h-28" aria-hidden="true">
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(60% 120% at 50% 50%, rgba(85,205,225,0.10) 0%, rgba(85,205,225,0) 70%)",
      }}
    />
  </div>
);

// The three lines that carry the most weight in the canonical source get the major treatment.
const MAJOR_QUOTE_CHAPTERS = ["recognition", "founder"];

const Chapter = ({ chapter }) => {
  const prose = chapter.blocks.filter((b) => b.type !== "quote");
  const quotes = chapter.blocks.filter((b) => b.type === "quote");
  const weight = MAJOR_QUOTE_CHAPTERS.includes(chapter.id) ? "major" : "minor";

  return (
    <section data-testid={`about-chapter-${chapter.id}`}>
      <motion.div {...fadeUp} className="px-6 sm:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-xl lg:max-w-[38rem] mx-auto">
          <h2
            className="text-[1.375rem] sm:text-[1.625rem] lg:text-[1.875rem] leading-[1.25] mb-1"
            style={{ fontFamily: SERIF, fontWeight: 300, color: IVORY }}
          >
            {chapter.title}
          </h2>
          {chapter.subtitle && (
            <p
              className="text-[11px] uppercase tracking-[0.24em] mb-7"
              style={{ fontFamily: BODY, color: "rgba(232,197,143,0.75)" }}
            >
              {chapter.subtitle}
            </p>
          )}
          <div className={chapter.subtitle ? "" : "mt-7"}>
            {prose.map((block, i) =>
              block.type === "lines" ? (
                <Lines key={i} items={block.items} />
              ) : (
                <Paragraph key={i}>{block.text}</Paragraph>
              )
            )}
          </div>
        </div>
      </motion.div>
      {quotes.map((q, i) => (
        <Interstitial key={i} text={q.text} weight={weight} />
      ))}
    </section>
  );
};

const AboutArvexisPage = () => {
  const [lang, setLang] = useState(() =>
    localStorage.getItem("arvexis_lang") === "es" ? "es" : "en"
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    setIsDesktop(mq.matches);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const onLang = () =>
      setLang(localStorage.getItem("arvexis_lang") === "es" ? "es" : "en");
    window.addEventListener("storage", onLang);
    window.addEventListener("arvexis:lang", onLang);
    return () => {
      window.removeEventListener("storage", onLang);
      window.removeEventListener("arvexis:lang", onLang);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const c = publicView(lang);
  const es = lang === "es";
  const openingQuote = c.opening.find((b) => b.type === "quote");
  const openingRest = c.opening.filter((b) => b !== openingQuote);
  const conclusion = openingRest[openingRest.length - 1];
  const openingBody = openingRest.slice(0, -1);

  const navLinks = [
    { to: "/", key: "home", label: es ? "Inicio" : "Home" },
    { to: "/privacy", key: "privacy", label: es ? "Privacidad" : "Privacy" },
  ];

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ background: BASE_NAVY }}
      data-testid="about-arvexis-public-page"
    >
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{ background: isDesktop ? DESKTOP_ATMOSPHERE : ATMOSPHERE }}
        aria-hidden="true"
        data-testid="about-public-atmosphere"
      />
      <motion.header
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-20 px-6 sm:px-8 py-6"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" aria-label="Arvexis" data-testid="about-public-logo-link">
            <ArvexisLogo size={48} />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                className="text-sm transition-colors tracking-wide"
                style={{ fontFamily: BODY, color: SILVER }}
                data-testid={`about-public-nav-${l.key}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="text-sm px-5 py-2 rounded-full border transition-colors"
              style={{ fontFamily: BODY, borderColor: "rgba(232,197,143,0.4)", color: CHAMPAGNE }}
              data-testid="about-public-signin-btn"
            >
              {es ? "Iniciar sesión" : "Sign In"}
            </Link>
          </nav>

          <button
            type="button"
            className="lg:hidden p-2"
            style={{ color: SILVER }}
            onClick={() => setMenuOpen(true)}
            aria-label={es ? "Abrir menú" : "Open menu"}
            data-testid="about-public-menu-btn"
          >
            <Menu size={24} />
          </button>
        </div>
      </motion.header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-50 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
          style={{ background: "rgba(3,10,18,0.97)" }}
          data-testid="about-public-menu-overlay"
        >
          <button
            type="button"
            className="absolute top-6 right-6 p-2"
            style={{ color: SILVER }}
            onClick={() => setMenuOpen(false)}
            aria-label={es ? "Cerrar menú" : "Close menu"}
            data-testid="about-public-menu-close-btn"
          >
            <X size={24} />
          </button>
          {navLinks.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className="text-2xl font-light tracking-wide"
              style={{ fontFamily: SERIF, color: IVORY }}
              data-testid={`about-public-menu-${l.key}`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            className="text-2xl tracking-wide"
            style={{ fontFamily: SERIF, color: CHAMPAGNE }}
            data-testid="about-public-menu-signin"
          >
            {es ? "Iniciar sesión" : "Sign In"}
          </Link>
        </div>
      )}

      <main className="relative z-10">
        {/* Hero — the orb appears once, here, and nowhere else on the page. */}
        <section
          className="px-6 sm:px-8 min-h-[72vh] sm:min-h-[80vh] flex flex-col items-center justify-center text-center"
          data-testid="about-public-hero"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <ArvexisPresence size="large" className="mx-auto mb-12 sm:mb-16" />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] mb-7"
            style={{ fontFamily: BODY, color: "rgba(232,197,143,0.85)" }}
          >
            {c.title}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl text-[1.75rem] sm:text-[2.5rem] lg:text-[3.5rem] leading-[1.2] tracking-[-0.015em]"
            style={{ fontFamily: SERIF, fontWeight: 300, color: IVORY }}
            data-testid="about-public-title"
          >
            {c.lede}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="mt-8 max-w-md text-[15px] sm:text-base leading-[1.8]"
            style={{ fontFamily: BODY, color: SILVER }}
            data-testid="about-public-sub"
          >
            {c.sub}
          </motion.p>
        </section>

        {/* Opening movement: the canonical question alone, then the reasoning, then the conclusion. */}
        {openingQuote && <Interstitial text={openingQuote.text} weight="major" />}

        <motion.section {...fadeUp} className="px-6 sm:px-8 pb-6" data-testid="about-public-opening">
          <div className="max-w-xl lg:max-w-[38rem] mx-auto">
            {openingBody.map((block, i) =>
              block.type === "lines" ? (
                <Lines key={i} items={block.items} />
              ) : (
                <Paragraph key={i}>{block.text}</Paragraph>
              )
            )}
          </div>
        </motion.section>

        {conclusion && <Interstitial text={conclusion.text} tone="gold" weight="major" />}

        <Breath />

        {c.chapters.map((chapter, i) => (
          <div key={chapter.id}>
            <Chapter chapter={chapter} />
            {(i + 1) % 5 === 0 && i < c.chapters.length - 1 && <Breath />}
          </div>
        ))}

        <motion.section
          {...fadeUp}
          className="px-6 sm:px-8 pt-24 pb-28 sm:pt-32 sm:pb-36"
          data-testid="about-public-closing"
        >
          <div className="max-w-2xl mx-auto text-center">
            <div
              className="w-10 h-px mx-auto mb-12"
              style={{ background: "rgba(232,197,143,0.45)" }}
              aria-hidden="true"
            />
            {c.closing.map((line, i) => (
              <p
                key={i}
                className="text-[1.25rem] sm:text-[1.5rem] lg:text-[1.875rem] leading-[1.5] mb-5 last:mb-0"
                style={{
                  fontFamily: SERIF,
                  fontWeight: 300,
                  color: i === c.closing.length - 1 ? CHAMPAGNE : IVORY,
                }}
              >
                {line}
              </p>
            ))}
          </div>
        </motion.section>
      </main>

      <footer className="relative z-10 px-6 py-10 border-t border-white/5">
        <div
          className="max-w-2xl mx-auto flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-xs"
          style={{ fontFamily: BODY, color: "rgba(128,149,166,0.8)" }}
        >
          <Link to="/" data-testid="about-public-footer-home">
            {es ? "Inicio" : "Home"}
          </Link>
          <a href="/privacy" data-testid="about-public-footer-privacy">
            {es ? "Privacidad" : "Privacy"}
          </a>
          <a href="/terms" data-testid="about-public-footer-terms">
            {es ? "Términos" : "Terms"}
          </a>
          <span>© 2026 Arvexis</span>
        </div>
      </footer>
      <BackToTop lang={lang} />
    </div>
  );
};

export default AboutArvexisPage;
